import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import CourseThumbnailUpload from '@/components/admin/CourseThumbnailUpload'
import type { Database } from '@/lib/supabase/supabase-types'

type Course = Database["public"]["Tables"]["courses"]["Row"]
type CourseInsert = Database["public"]["Tables"]["courses"]["Insert"]

interface CourseFormProps {
  course?: Course | null
  onSubmit: (data: CourseInsert | { id: string } & Partial<Course>) => void
  isLoading?: boolean
  onCancel: () => void
}

export default function CourseForm({ course, onSubmit, isLoading, onCancel }: CourseFormProps) {
  const [formData, setFormData] = useState({
    title: course?.title || '',
    slug: course?.slug || '',
    description: course?.description || '',
    thumbnail_url: course?.thumbnail_url || '',
    is_published: course?.is_published || false,
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (course) {
      // Update existing course
      onSubmit({ id: course.id, ...formData })
    } else {
      // Create new course
      onSubmit(formData)
    }
  }

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
  }

  const handleTitleChange = (title: string) => {
    setFormData(prev => ({
      ...prev,
      title,
      // Auto-generate slug only for new courses if slug is empty
      slug: !course && !prev.slug ? generateSlug(title) : prev.slug
    }))
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => handleTitleChange(e.target.value)}
          placeholder="Course title"
          required
        />
      </div>

      <div>
        <Label htmlFor="slug">Slug</Label>
        <Input
          id="slug"
          value={formData.slug}
          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
          placeholder="course-slug"
          required
        />
        <p className="text-sm text-muted-foreground mt-1">
          Used in the URL: /course/{formData.slug || 'course-slug'}
        </p>
      </div>

      <div>
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          placeholder="Course description"
          rows={4}
        />
      </div>

      <CourseThumbnailUpload
        courseId={course?.id}
        currentUrl={formData.thumbnail_url}
        onChange={(url) => setFormData(prev => ({ ...prev, thumbnail_url: url || '' }))}
      />

      <div className="flex items-center space-x-2">
        <Switch
          id="is_published"
          checked={formData.is_published}
          onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_published: checked }))}
        />
        <Label htmlFor="is_published">Published</Label>
      </div>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Saving...' : course ? 'Update Course' : 'Create Course'}
        </Button>
      </div>
    </form>
  )
}